<template>
  <teleport to="head">
    <MetaTags
      :title="`DNS ${recordType.upper} records for ${domain}`"
      :description="`These are all the ${recordType.plural} (${recordType.upper} records) for ${domain} according to the authoritative DNS server.`"
      :url="`https://www.nslookup.io${domainToUrl(domainPunycode)}`"
    />
  </teleport>
  <HeaderComponent :domain="domain" :domain-search-to-url="domainToUrl"
                   :search-button-text="['soa', 'spf', 'cname'].includes(recordType.lower) ? `Find ${recordType.upper} record` : `Find ${recordType.upper} records`" />
  <div class="flex-grow bg-neutral-100">
    <main class="container mx-auto max-w-6xl">
      <div class="flex flex-col justify-between mt-8 mb-6 ml-6 sm:flex-row sm:items-center">
        <h1 class="text-3xl">{{recordType.upper}} {{ ['soa', 'spf', 'cname'].includes(recordType.lower) ? 'record' : 'records' }} for <b v-if="domain === domainPunycode">{{ domain }}</b><span v-else><b>{{ domainPunycode }}</b> ({{ domain }})</span></h1>
        <dns-records-link :domain="domain" class="shrink-0 hidden mr-6 sm:block xl:mr-0">
          <button type="button" class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">All DNS records</button>
        </dns-records-link>
      </div>
      <div class="bg-white shadow p-6 pb-10 rounded-t">
        <ApiErrorAlert v-if="error !== null" :error="error" :domain="domain" dnsserver="authoritative" />
        <div v-else-if="loading" aria-hidden="true">
          <SkeletonComponent class="w-56 h-8 mb-6" />
          <div v-for="n in 2" :key="n">
            <SkeletonComponent v-for="n in 2" :key="n" class="inline-block w-1/2 h-4 pr-[10%] bg-clip-content" />
          </div>
          <SkeletonComponent class="inline-block w-56 h-8 my-6" />
          <SkeletonComponent class="w-24 h-4 mb-2" />
          <SkeletonComponent class="w-1/2 h-4 mb-6" />
          <template v-for="n in 3" :key="n">
            <SkeletonComponent class="w-24 h-4 mb-2 block" />
            <SkeletonComponent class="w-1/2 h-4 mb-2 block" />
            <SkeletonComponent class="w-1/2 h-4 mb-6" />
          </template>
        </div>
        <template v-else>
          <p class="mb-3">An authoritative DNS server ({{ queryResult.query.server.domain }}) responded with these DNS records when we queried it for
            the domain {{ domain }}.</p>
          <div v-if="queryResult.response.answer.length === 0">
            <Alert type="warning">
              <span><span class="font-semibold">{{ domain }}</span> does not have any {{recordType.upper}} records.</span>
            </Alert>
          </div>
          <ip-dns-records class="w-full" v-else-if="recordType.upper === 'A'" :records="queryResult" column-title="IPv4 address" record-type="A" />
          <ip-dns-records class="w-full" v-else-if="recordType.upper === 'AAAA'" :records="queryResult" column-title="IPv6 address" record-type="AAAA" />
          <caa-records class="w-full" v-else-if="recordType.upper === 'CAA'" :records="queryResult" :domain="domain" />
          <dns-records class="w-full" v-else-if="recordType.upper === 'CNAME'" :records="queryResult" column-title="Canonical name" record-type="CNAME" :is-domain="true" />
          <txt-records class="w-full" v-else-if="recordType.upper === 'TXT'" :records="queryResult" :domain="domain" />
          <ns-records class="w-full" v-else-if="recordType.upper === 'NS'" :records="queryResult" :domain="domain" />
          <ptr-records class="w-full" v-else-if="recordType.upper === 'PTR'" :records="queryResult" :domain="domain" />
          <mx-records class="w-full" v-else-if="recordType.upper === 'MX'" :records="queryResult" />
          <soa-records class="w-full" v-else-if="recordType.upper === 'SOA'" :records="queryResult" :domain="domain" />
          <srv-records class="w-full" v-else-if="recordType.upper === 'SRV'" :records="queryResult" :domain="domain" />
          <tlsa-records class="w-full" v-else-if="recordType.upper === 'TLSA'" :records="queryResult" :domain="domain" />
          <spf-records class="w-full" v-else-if="recordType.upper === 'SPF'" :records="queryResult" :domain="domain" />
          <dns-records class="w-full" v-else :records="queryResult" column-title="Data" :record-type="recordType.upper" />
          <AdUnit class="mt-8 mx-auto" :config="RecordTypeResults" />
          <h2 class="mt-8 mb-2 font-semibold text-2xl">Question and response</h2>
          <raw-records :records="queryResult" />
        </template>
        <h2 class="mt-8 mb-2 font-semibold text-2xl">Find {{ recordType.upper }} records for another domain name</h2>
        <div class="flex flex-col lg:flex-row">
          <p class="shrink max-w-xl">With <router-link :to="`/${recordType.lower}-lookup/`" class="text-blue-600 hover:text-blue-800 hover:underline">{{recordType.upper}}
            lookup</router-link>, you can find the {{ recordType.upper }} record for any domain name or subdomain. When you enter the domain in
            the input field below, it will show whether or not {{ recordType.plural }} are configured for that domain.</p>
          <form class="mt-3 w-[500px] max-w-full sm:px-6 lg:mt-0" autocomplete="off">
            <DomainSearch :placeholder-text="recordType.upper === 'PTR' ? 'IP address or domain name' : 'Domain name'"
                          emphasize :buttonText="`Find ${recordType.upper} record`" :prefill="domain" @submitted="redirectTo" />
          </form>
        </div>
      </div>
    </main>
  </div>
  <FooterComponent />
</template>

<script setup lang="ts">
import {
  computed, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import axios, { Canceler } from 'axios';
import { domainNameToDisplayName, domainNameToUrlPath, urlPathToDomainName } from '@/common/domainName';
import HeaderComponent from '@/components/layout/HeaderComponent.vue';
import FooterComponent from '@/components/layout/FooterComponent.vue';
import DomainSearch from '@/components/DomainSearch.vue';
import Alert from '@/components/micro/AlertComponent.vue';
import DnsRecordsLink from '@/components/micro/DnsRecordsLink.vue';
import { getRecordType } from '@/common/recordTypes';
import RawRecords from '@/components/micro/RawRecords.vue';
import DnsRecords from '@/components/records/DnsRecords.vue';
import SoaRecords from '@/components/records/SoaRecords.vue';
import IpDnsRecords from '@/components/records/IpDnsRecords.vue';
import TxtRecords from '@/components/records/TxtRecords.vue';
import NsRecords from '@/components/records/NsRecords.vue';
import MxRecords from '@/components/records/MxRecords.vue';
import MetaTags from '@/components/MetaTags.vue';
import SrvRecords from '@/components/records/SrvRecords.vue';
import CaaRecords from '@/components/records/CaaRecords.vue';
import PtrRecords from '@/components/records/PtrRecords.vue';
import TlsaRecords from '@/components/records/TlsaRecords.vue';
import SkeletonComponent from '@/components/micro/SkeletonComponent.vue';
import { ApiErrorType, toErrorType } from '@/model/ApiErrorAlert';
import ApiErrorAlert from '@/components/ApiErrorAlert.vue';
import SpfRecords from '@/components/records/SpfRecords.vue';
import {
  OtherRecordsResponse, QueryResult, emptyDnsServer, emptyResponse,
} from '@/model/Api';
import {
  Billboard, LargeLeaderboard, LargeRectangle, Leaderboard, MediumRectangle,
} from '@/common/adSizes';
import AdUnit from '@/components/ads/AdUnit.vue';
import { RecordTypeResults } from '@/common/adSlots';

const props = defineProps({
  routerDomain: {
    type: String,
    required: true,
  },
  routerRecordType: {
    type: String,
    required: true,
  },
});

const router = useRouter();
const recordType = computed(() => getRecordType(props.routerRecordType));

const domain = ref('');
const domainPunycode = ref('');
const loading = ref(true);
const error = ref<ApiErrorType | null>(null);
const showSidebar = ref(window.innerWidth > 991);
const cancelToken = ref<Canceler | null>(null);
const queryResult = ref<QueryResult>({
  query: {
    server: emptyDnsServer,
    domain: domain.value,
    recordType: recordType.value.upper,
  },
  response: emptyResponse,
});

function domainToUrl(newDomain: string): string {
  if (recordType.value.upper === 'SPF') {
    return `/domains/${domainNameToUrlPath(newDomain)}/email/${recordType.value.lower}/`;
  }
  return `/domains/${domainNameToUrlPath(newDomain)}/dns-records/${recordType.value.lower}/`;
}

function redirectTo(newDomain: string): void {
  router.push(domainToUrl(newDomain));
}

function load() {
  loading.value = true;
  error.value = null;

  if (cancelToken.value) {
    cancelToken.value();
  }
  const axiosSource = axios.CancelToken.source();
  cancelToken.value = axiosSource.cancel;

  // Normally, we want to resolve the hostname of an IP address (which is what `/other-records` does).
  // But when looking up a PTR, we should show the result of querying the PTR records.
  const endpoint = recordType.value.upper === 'PTR' ? 'api/v1/ptr-records' : 'api/v1/other-records';

  axios
    .post<OtherRecordsResponse>(endpoint, {
      domain: domain.value,
      dnsServer: 'authoritative',
      recordType: recordType.value.cli,
    }, {
      cancelToken: axiosSource.token,
    })
    .then((response) => {
      if (props.routerDomain !== domainNameToUrlPath(response.data.punycodeDomain)) {
        router.replace({
          path: domainToUrl(response.data.punycodeDomain),
        });
      }
      domain.value = domainNameToDisplayName(response.data.unicodeDomain);
      domainPunycode.value = domainNameToDisplayName(response.data.punycodeDomain);
      queryResult.value = response.data.queryResult;
      loading.value = false;
    })
    .catch((e) => {
      if (!axios.isCancel(e)) {
        error.value = toErrorType(e.response.data.error);
      }
    });
}

watch(() => props.routerDomain, () => {
  const domainName = urlPathToDomainName(props.routerDomain);
  domain.value = domainName;
  domainPunycode.value = domainName;
  load();
}, {
  immediate: true,
});

window.addEventListener('resize', () => {
  showSidebar.value = window.innerWidth > 991;
});
</script>
